import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLayer } from './layers';

interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  testId?: string;
  /** Kein Schließen per Klick auf den Hintergrund (z. B. bei ungespeicherten Änderungen). */
  sticky?: boolean;
}

/** Barrierearmer Dialog: Fokusfalle, Escape schließt, Fokus kehrt zurück. */
export function Modal({ title, onClose, children, footer, wide, testId = 'modal', sticky }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const isTop = useLayer();
  // onClose ist meist inline – über eine Ref lesen, damit der Effekt (Fokus setzen!) nur beim Öffnen läuft.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[autofocus], input, textarea, select, button:not([data-close])');
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      }
      if (e.key === 'Tab' && el) trapFocus(el, e);
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [isTop]);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => !sticky && e.target === e.currentTarget && onClose()} data-testid={`${testId}-backdrop`}>
      <div ref={ref} className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid={testId}>
        <div className="modal-header">
          <h2 id={titleId}>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Schließen" data-close data-testid={`${testId}-close`}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function trapFocus(container: HTMLElement, e: KeyboardEvent): void {
  const items = [...container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])')].filter(
    (x) => x.offsetParent !== null || x === document.activeElement,
  );
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

interface ConfirmProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}

export function ConfirmDialog({ title, message, confirmLabel = 'Bestätigen', danger, busy, onConfirm, onClose, children }: ConfirmProps) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      testId="confirm-dialog"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} data-testid="confirm-cancel">
            Abbrechen
          </button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm} disabled={busy} data-testid="confirm-ok">
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="stack">
        <div>{message}</div>
        {children}
      </div>
    </Modal>
  );
}
